import CbError from "@/utils/cb-error"
import TraceKit from "tracekit"
import LogDumper from "@/models/log-dumper"
import Raven from 'raven-js';
import { uuid } from "@/utils/cbjs-helper";
import { logError as sendStructuredError } from "@/loggers/error-logger";
import { logDebug as sendStructuredDebug } from "@/loggers/debug-logger";

class LogData {
  constructor() {
    this.version = 1;
    this.server_version = 1;
    this.meta = {};
    this.data = {};
    this.id = uuid();
    this.logDumper = new LogDumper(this, 5000);
  }

  payload() {
    var pl = {type: 'info', info: this.data, id: this.id, meta: this.meta};
    // reset data
    this.data = {};
    return pl;
  }

  send(type) {
    var pl = {type: type, data: this.data, id: this.id, meta: this.meta};
    this.data = {};
    this.meta = {};
    this.logDumper.sendPayLoad(pl).catch(() => {
      // Avoids unhandled error when error tracking API is blocked
    });
  }

  sendError(error) {
    var pl = {error: error, type: 'error', id: this.id, meta: this.meta};
    this.logDumper.sendPayLoad(pl).catch(() => {
      // Avoids unhandled error when error tracking API is blocked
    });
  }

  append(key, value) {
    this.data[key] = this.data[key] || [];
    this.data[key].push(value);
    this.version += 1;
  }

  add(log) {
    Object.assign(this.data, log);
    this.version += 1;
  }
}


class Logger {
  static get DEBUG() {
    return 0;
  }

  static get INFO() {
    return 1;
  }

  static get WARN() {
    return 2;
  }

  static get ERROR() {
    return 3;
  }

  constructor(mode, consoleLogEnabled, sentry) {
    this.mode = mode;
    this.consoleLogEnabled = consoleLogEnabled;
    this.logData = new LogData();
    if(sentry) {
      this.sentry = sentry;
      // Release comes from the build tag (CB_BUILD_TAG), set by `npm run build <tag>` in CI.
      const sentryOptions = {
        ...this.sentry.options,
        release: process.env.CB_BUILD_TAG,
        sampleRate: 0.2
      };
      Raven.config(this.sentry.dsn, sentryOptions)
          .install();
      Raven.setTagsContext(this.sentry.tags);
    }
  }

  // hosted page id
  addMetaData(key, value) {
    this.logData.meta[key] = value;
  }


  d(log) {
    if(this.mode <= Logger.DEBUG) {
      this._l(log)
    }
  }

  i(log) {
    if(this.mode <= Logger.INFO) {
      this._l(log)
    }
  }

  a(key, value) {
    if(this.mode <= Logger.INFO) {
      this._a(key, value)
    }
  }

  w(log) {
    if(this.mode <= Logger.WARN) {
      this._l(log)
    }
  }

  e(error) {
    if(this.mode <= Logger.ERROR) {
      this._e(error);
    }
  }

  /**
   * Structured error logging: sends to hp-service /log/error with error_code, meta, and optional cause.
   * Also sends Error instances to Sentry/TraceKit via _e. Does not rethrow (matches cb-hp-ui).
   * @param {string} code - Error code (e.g. ApiErrorCode.API_REQUEST_FAILED)
   * @param {Record<string, unknown>} [payload] - Meta payload (default {})
   * @param {unknown} [cause] - Original error (Error, CbError, etc.)
   */
  error(code, payload = {}, cause) {
    if (this.mode <= Logger.ERROR) {
      sendStructuredError(code, payload && typeof payload === 'object' ? payload : {}, cause);
      if (cause instanceof Error) {
        this._e(cause);
      }
    }
  }

  /**
   * Structured debug logging: sends to hp-service /log/debug with debug_code and meta.
   * Telemetry only (e.g. migration signals) — NOT sent to Sentry, separate from error counts.
   * @param {string} code - Debug code (e.g. DebugCode.VUEX_ILLEGAL_MUTATION)
   * @param {Record<string, unknown>} [payload] - Meta payload (default {})
   */
  debug(code, payload = {}) {
    if (this.mode <= Logger.ERROR) {
      sendStructuredDebug(code, payload && typeof payload === 'object' ? payload : {});
    }
  }

  _l(log) {
    if(this.consoleLogEnabled) {
      // console.log(Date.now(), log);
    }

    if(typeof log === 'object') {
      this.logData.add(log);
    }
  }

  _a(key, value) {
    this.logData.append(key, value);
  }

  _e(error) {
    // dont send if the following conditions are met 
    // error is instance of cberror(Meaning server send error)
    // error type is internal (form errors);
    if(!((error instanceof CbError && !error.notifyBackend) || error.type == "internal")){
      // trackjs
      // server send
      try {
        // if the error is not an error object, tracekit is rethrowing error
        TraceKit.report(error);
      }
      catch(error) {

      }
      if(this.sentry) {
        Raven.captureException(error);
      }
    }

    if(process.env.NODE_ENV != "production") {
      throw error;
    }
  }

  kvl(data) {
    if(data && typeof data == "object" && Object.keys(data).length > 0) {
      this.i(data);
      this.logData.send('kvl');
    }
  }
}

TraceKit.remoteFetching = false;
TraceKit.report.subscribe((errorReport) => {
  // if(logger.sentry) {
  //   Raven.captureMessage(JSON.stringify(errorReport));
  // }
  logger.logData.sendError(errorReport);
});


export default Logger